import React from 'react';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { buildWelcomeBackContent } from './welcomeBackContent';

// Guarded Ahoy tracking — mirrors the container's pattern so analytics being
// unavailable (or SSR) is a no-op rather than a throw.
const track = (event) => {
  if (typeof window !== 'undefined' && window.ahoy && typeof window.ahoy.track === 'function') {
    window.ahoy.track(event);
  }
};

// Welcome-back intro screen ([C]). One-way: rendered before StepMotivation when
// the `modernized_onboarding_welcome_back` toggle is on; the CTA advances into
// the existing step flow and there is no path back to this screen.
export default function StepWelcomeBack({ brandedName, showApprovalFyi = true, onContinue }) {
  const content = buildWelcomeBackContent(brandedName);

  React.useEffect(() => {
    track('modernized_onboarding_welcome_back_viewed');
  }, []);

  const handleContinue = () => {
    track('modernized_onboarding_welcome_back_dismissed');
    if (typeof onContinue === 'function') onContinue();
  };

  return (
    <div className="w-full px-8 py-6 sm:px-12 sm:py-8">
      <div className="w-full max-w-3xl mx-auto text-center">
        {/* Header */}
        <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4 tracking-tight">
          {content.headline}
        </h2>
        <p className="text-lg text-gray-500 mb-6">{content.subheadline}</p>

        {/* Benefits */}
        <ul className="text-left max-w-xl mx-auto space-y-4 mb-6">
          {content.benefits.map((benefit) => (
            <li key={benefit} className="flex items-start gap-3">
              <span
                className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full"
                style={{ backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))' }}>
                <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
              </span>
              <span className="text-base leading-relaxed text-gray-700">{benefit}</span>
            </li>
          ))}
        </ul>

        {/* Manager-approval FYI — only when an approval workflow is enabled.
            Bolded callout so the review caveat clearly stands out. */}
        {showApprovalFyi && (
          <div className="max-w-xl mx-auto mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900 text-center">{content.fyi}</p>
          </div>
        )}

        {/* CTA */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleContinue}
            className="inline-flex items-center gap-2 px-10 py-4 text-white font-medium text-lg transition-all duration-200 group rounded-full"
            style={{ backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}>
            {content.cta}
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </div>
    </div>
  );
}
